import { useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtDateTime } from "@/lib/api";

const cls = "w-full rounded-md border border-[#D4E6EC] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B6E8F]/30";

function NewUser({ open, setOpen, onDone }) {
  const [f, setF] = useState({ name: "", email: "", password: "", role: "staff" });
  const submit = async () => {
    try {
      await api.post("/users", f);
      toast.success("Utente creato");
      setF({ name: "", email: "", password: "", role: "staff" });
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="new-user-dialog">
        <DialogHeader><DialogTitle className="font-serif-it text-2xl">Nuovo utente</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <input className={cls} placeholder="Nome e cognome" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="new-user-name" />
          <input className={cls} placeholder="Email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="new-user-email" />
          <input className={cls} placeholder="Password iniziale (min. 8 caratteri)" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="new-user-password" />
          <select className={cls} value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} data-testid="new-user-role">
            <option value="staff">Staff banqueting</option><option value="admin">Amministratore</option>
          </select>
        </div>
        <DialogFooter><Button onClick={submit} data-testid="new-user-submit">Crea utente</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Users() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/users").then((r) => setRows(r.data));
  useEffect(() => {
    load();
  }, []);

  const patch = async (id, body, msg) => {
    try { await api.patch(`/users/${id}`, body); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const resetPwd = (u) => {
    const p = window.prompt(`Nuova password per ${u.email} (min. 8 caratteri)`);
    if (p) patch(u.id, { password: p }, "Password aggiornata");
  };

  return (
    <div className="space-y-8 fade-up">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#7B929C]">Amministrazione</div>
          <h1 className="font-serif-it text-4xl sm:text-5xl font-semibold mt-2">Utenti dello staff</h1>
          <p className="text-sm text-[#47606B] mt-1">Solo le persone aggiunte qui possono accedere all'app.</p>
        </div>
        <Button className="rounded-full" onClick={() => setOpen(true)} data-testid="new-user-button"><UserPlus size={15} className="mr-2" /> Nuovo utente</Button>
      </div>
      <div className="rounded-xl border border-[#D4E6EC] bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="users-table">
          <thead className="text-left text-[11px] uppercase tracking-wider text-[#7B929C] border-b border-[#D4E6EC]">
            <tr><th className="p-4">Nome</th><th className="p-4">Email</th><th className="p-4">Ruolo</th><th className="p-4">Creato</th><th className="p-4">Stato</th><th className="p-4"></th></tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.id} className="border-b border-[#EAF5F7]" data-testid={`user-row-${u.email}`}>
                <td className="p-4 font-medium">{u.name}</td>
                <td className="p-4">{u.email}</td>
                <td className="p-4">{u.role === "admin" ? "Amministratore" : "Staff"}</td>
                <td className="p-4 text-xs">{fmtDateTime(u.created_at)}</td>
                <td className="p-4">{u.active ? <span className="text-[#166534]">Attivo</span> : <span className="text-[#991B1B]">Disabilitato</span>}</td>
                <td className="p-4 text-right whitespace-nowrap space-x-2">
                  <Button variant="ghost" size="sm" onClick={() => resetPwd(u)} data-testid={`user-reset-${u.email}`}><KeyRound size={14} className="mr-1" /> Password</Button>
                  {u.id !== user.id && <Button variant="outline" size="sm" onClick={() => patch(u.id, { active: !u.active }, u.active ? "Utente disabilitato" : "Utente riattivato")} data-testid={`user-toggle-${u.email}`}>{u.active ? "Disabilita" : "Riattiva"}</Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <NewUser open={open} setOpen={setOpen} onDone={load} />
    </div>
  );
}
